import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { taskNavRef } from '../utils/taskNav';

/**
 * Страница «Мои задачи» для учеников: список созданных ими задач (черновики) и форма создания новой.
 */
export default function MyTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    slug: '',
    title: '',
    statement: '',
    answer_type: 'text',
    expected_answer: '',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    fetchMyTasks();
  }, []);

  async function fetchMyTasks() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/tasks/my-tasks/');
      setTasks(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch (e) {
      setError(e.response?.status === 403 ? 'Доступ запрещён' : 'Не удалось загрузить список');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    try {
      await api.post('/tasks/my-tasks/', {
        slug: form.slug || undefined,
        title: form.title || 'Черновик задачи',
        statement: form.statement || '',
        answer_type: form.answer_type || 'text',
        expected_answer: form.expected_answer || '',
      });
      setForm({ slug: '', title: '', statement: '', answer_type: 'text', expected_answer: '' });
      await fetchMyTasks();
    } catch (e) {
      setCreateError(e.response?.data?.slug?.[0] || e.response?.data?.detail || 'Ошибка создания');
    } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="my-tasks-page">
      <h1>Мои задачи</h1>
      <p className="text-secondary">
        Здесь отображаются задачи, созданные вами (черновики). Они видны только вам.
      </p>

      <section className="my-tasks-create">
        <h2>Создать свою задачу</h2>
        <form onSubmit={handleCreate} className="my-tasks-form">
          {createError && <div className="alert alert-error">{createError}</div>}
          <div className="form-group">
            <label>Slug (латиница, дефисы)</label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="my-task"
            />
          </div>
          <div className="form-group">
            <label>Название</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Название задачи"
            />
          </div>
          <div className="form-group">
            <label>Условие (Markdown)</label>
            <textarea
              value={form.statement}
              onChange={(e) => setForm((f) => ({ ...f, statement: e.target.value }))}
              placeholder="Текст условия задачи"
              rows={4}
            />
          </div>
          <div className="form-group">
            <label>Тип ответа</label>
            <select
              value={form.answer_type}
              onChange={(e) => setForm((f) => ({ ...f, answer_type: e.target.value }))}
            >
              <option value="code">Код</option>
              <option value="text">Текст</option>
              <option value="number">Число</option>
            </select>
          </div>
          <div className="form-group">
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? 'Создание...' : 'Создать черновик'}
            </button>
          </div>
        </form>
      </section>

      <section className="my-tasks-list">
        <h2>Список моих задач</h2>
        {error && <div className="alert alert-error">{error}</div>}
        {tasks.length === 0 ? (
          <p className="text-secondary">У вас пока нет созданных задач.</p>
        ) : (
          <ul className="task-list-simple">
            {tasks.map((t) => (
              <li key={t.id}>
                <Link to={`/work/${taskNavRef(t)}`}>{t.title || taskNavRef(t)}</Link>
                <span className="badge">{t.status === 'draft' ? 'Черновик' : t.status}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
